import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddElementsView } from "./views/addElements/AddElementsView";
import { AddPageView } from "./views/addPage/AddPageView";
import { AddSectionView } from "./views/addSectionMenu/AddSectionView";
import { MediaView } from "./views/media/MediaView";
import { ThemesView } from "./views/design/themes/ThemesView";
import { ColorsView } from "./views/design/colors/ColorsView";
import { FontsView } from "./views/design/fonts/FontsView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftToLine, faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { NavigationView } from "./views/navigation/NavigationView";
import "./websiteBuilderToolbarMenu.scss";
import { useWebsiteBuilderToolbarContext } from "../websiteBuilderToolbarContext";

export type TWebsiteBuilderToolbarMenuType = "add-elements" | "add-page" | "add-section" | "design" | "media" | "none";

interface IWebsiteBuilderToolbarMenuView {
  title: string;
  render: () => React.ReactNode;
  subView?: boolean;
}

export function WebsiteBuilderToolbarMenu() {
  const { toolbarState, handleSetToolbarMenuType } = useWebsiteBuilderToolbarContext();
  const { t } = useTranslation();

  const [viewIndex, setViewIndex] = useState<number>(0);

  function createSubViews(
    navigationViewTitle: string,
    views: IWebsiteBuilderToolbarMenuView[],
  ): IWebsiteBuilderToolbarMenuView[] {
    return [
      {
        title: navigationViewTitle,
        render: () => (
          <NavigationView
            subViewOptions={views.map((view, index) => ({ title: view.title, index: index + 1 }))}
            setViewIndex={setViewIndex}
          />
        ),
      },
      ...views.map(view => ({ ...view, subView: true })),
    ];
  }

  const views: IWebsiteBuilderToolbarMenuView[] = useMemo(() => {
    switch (toolbarState.menuType) {
      case "add-elements":
        return [
          { title: t("Add Elements"), render: () => <AddElementsView /> },
        ];
      case "add-page":
        return [
          { title: t("Add Page"), render: () => <AddPageView /> },
        ];
      case "add-section":
        return [
          { title: t("Add Section"), render: () => <AddSectionView /> },
        ];
      case "design":
        return createSubViews("Design", [
          { title: t("Themes"), render: () => <ThemesView /> },
          { title: t("Colors"), render: () => <ColorsView /> },
          { title: t("Fonts"), render: () => <FontsView /> },
        ]);
      case "media":
        return [
          { title: t("Media"), render: () => <MediaView /> },
        ];
      case "none":
      default: {
        return [];
      }
    }
  }, [toolbarState.menuType]);

  useEffect(() => {
    setViewIndex(0);
  }, [views]);

  const view = views?.[viewIndex];

  if (!view) {
    return null;
  }

  return (
    <div>
      <div className="website-builder-toolbar-menu-header">
        <div className="toolbar-menu-header-heading-container">
          {view.subView && (
            <FontAwesomeIcon
              className="toolbar-menu-header-chevron-icon"
              icon={faChevronLeft}
              onClick={() => setViewIndex(0)}
            />
          )}
          <h3>{view.title}</h3>
        </div>
        <FontAwesomeIcon
          icon={faArrowLeftToLine}
          className="toolbar-menu-header-arrow-left-icon"
          onClick={() => handleSetToolbarMenuType("none")}
        />
      </div>
      {view.render()}
    </div>
  );
}
